import type { ConversaDetalhe, StatusPresenca } from '@mymaxx/shared';
import { Aviso, Botao } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Ear, Share2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { ModalPausa } from '../atendimento/ModalPausa';
import { BarraAtendente } from '../atendimento/BarraAtendente';
import { CabecalhoConversa } from '../atendimento/CabecalhoConversa';
import { Conversa } from '../atendimento/Conversa';
import { PainelCliente } from '../atendimento/PainelCliente';
import { ModalEncerrar, ModalTransferir } from '../atendimento/ModaisAtendimento';
import { ModalVincular } from '../atendimento/ModalVincular';
import { authApi, ErroApi } from '../lib/api';
import { atendimentoApi } from '../lib/atendimento-api';
import { useSessao } from '../lib/sessao';
import { useSussurros } from '../lib/sussurros';
import { useTempoRealEquipe } from '../lib/tempo-real';
import { useNavigate } from 'react-router';

/**
 * Estação de trabalho do atendente: três colunas em tela cheia, sem menu lateral.
 * A fila não é listada — ele pede o próximo cliente e o sistema entrega (decisão de
 * 24/09, no modelo do Blip Desk). Quem também supervisiona ou administra continua na
 * tela de Atendimentos dentro do painel, porque precisa navegar para outros lugares.
 */
export function Estacao() {
  const queryClient = useQueryClient();
  const navegar = useNavigate();
  const usuario = useSessao((e) => e.usuario);
  const limpar = useSessao((e) => e.limpar);
  const abrirSussurro = useSussurros((e) => e.abrir);

  const [selecionada, setSelecionada] = useState<string | null>(null);
  /** Protocolo antigo ocupando o centro da tela; o X devolve o atendimento em curso. */
  const [anteriorId, setAnteriorId] = useState<string | null>(null);
  // A presença vem do servidor no login/refresh. Recarregar a página não pode mudar
  // onde a pessoa está — só entrar na estação (abaixo) e a escolha dela.
  // 'offline' como reserva, nunca 'online': na dúvida a tela não pode dizer que a pessoa
  // está pronta para receber cliente.
  const [status, setStatus] = useState<StatusPresenca>(usuario?.statusPresenca ?? 'offline');
  const [motivoPausa, setMotivoPausa] = useState<string | null>(usuario?.motivoPausa ?? null);
  // Invisibilidade é outra coisa que o plantão: quem está invisível continua recebendo
  // cliente da fila. Uma é sobre ser visto, a outra é sobre trabalhar.
  const [invisivel, setInvisivel] = useState(usuario?.invisivel ?? false);
  const [pausaDesde, setPausaDesde] = useState<string | null>(usuario?.pausaIniciadaEm ?? null);
  const [aviso, setAviso] = useState<{ tipo: 'sucesso' | 'erro'; texto: string } | null>(null);
  const [transferindo, setTransferindo] = useState(false);
  const [encerrando, setEncerrando] = useState(false);
  const [vinculando, setVinculando] = useState(false);

  const resumo = useQuery({
    queryKey: ['atendimento-resumo'],
    queryFn: atendimentoApi.resumo,
    refetchInterval: 30_000,
  });
  const minhas = useQuery({ queryKey: ['atendimento-minhas'], queryFn: atendimentoApi.minhas });
  const desempenho = useQuery({
    queryKey: ['meu-desempenho'],
    queryFn: atendimentoApi.desempenho,
    // A nota só muda quando um cliente avalia, o que acontece depois do encerramento.
    refetchInterval: 60_000,
  });
  const detalhe = useQuery({
    queryKey: ['conversa', selecionada],
    queryFn: () => atendimentoApi.detalhe(selecionada!),
    enabled: !!selecionada,
  });
  const anterior = useQuery({
    queryKey: ['conversa-anterior', selecionada, anteriorId],
    queryFn: () => atendimentoApi.anterior(selecionada!, anteriorId!),
    enabled: !!selecionada && !!anteriorId,
  });
  // Uma chamada só, e por uma rota que o atendente pode ler: as de /v1/gestao exigem
  // permissão de administração e devolviam 403 para quem só atende.
  const catalogos = useQuery({
    queryKey: ['catalogos-atendimento'],
    queryFn: atendimentoApi.catalogos,
  });
  const setores = { data: catalogos.data?.setores };
  const rapidas = { data: catalogos.data?.mensagensRapidas };
  const motivos = { data: catalogos.data?.motivos };

  const recarregar = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['atendimento-minhas'] });
    void queryClient.invalidateQueries({ queryKey: ['atendimento-resumo'] });
    void queryClient.invalidateQueries({ queryKey: ['conversa'] });
    void queryClient.invalidateQueries({ queryKey: ['meu-desempenho'] });
  }, [queryClient]);

  useTempoRealEquipe(selecionada, { aoReceberMensagem: recarregar, aoMudarConversa: recarregar });

  /*
   * Abrir a estação NÃO coloca ninguém em disponível: quem entra começa ausente e clica
   * quando estiver pronto. O contrário — entrar já recebendo — despeja a fila inteira em
   * cima de alguém que ainda está abrindo o sistema.
   *
   * O batimento que mantém viva a presença escolhida está na moldura (`useSinalDePresenca`),
   * para valer em qualquer tela e não só nesta.
   */

  // Sem nada selecionado, abre o primeiro da lista — a tela nunca fica vazia à toa.
  useEffect(() => {
    const lista = minhas.data ?? [];
    if (lista.length === 0) {
      if (selecionada) setSelecionada(null);
      return;
    }
    if (!selecionada || !lista.some((c) => c.id === selecionada)) {
      setSelecionada(lista[0]!.id);
      setAnteriorId(null);
    }
  }, [minhas.data, selecionada]);

  const trocarInvisivel = useMutation({
    mutationFn: (valor: boolean) => atendimentoApi.definirInvisivel(valor),
    onSuccess: (_, valor) => {
      setInvisivel(valor);
      setAviso({
        tipo: 'sucesso',
        texto: valor
          ? 'Você está invisível: ninguém vê se está online. As ligações continuam chegando.'
          : 'Você voltou a aparecer para a equipe.',
      });
    },
  });

  const presenca = useMutation({
    mutationFn: ({ novo, motivo }: { novo: StatusPresenca; motivo?: string }) =>
      atendimentoApi.definirPresenca(novo, motivo),
    onSuccess: (_, { novo, motivo }) => {
      setStatus(novo);
      setMotivoPausa(novo === 'pausa' ? (motivo ?? null) : null);
      setPausaDesde(novo === 'pausa' ? new Date().toISOString() : null);
      setAviso({
        tipo: 'sucesso',
        texto:
          novo === 'pausa'
            ? `Em pausa (${motivo}). Você não recebe da fila até voltar.`
            : 'Você está disponível.',
      });
    },
  });

  const puxar = useMutation({
    mutationFn: () => atendimentoApi.puxarProxima(),
    onSuccess: (conversa) => {
      setSelecionada(conversa.id);
      setAnteriorId(null);
      recarregar();
      setAviso(null);
    },
    onError: (e) =>
      setAviso({
        tipo: 'erro',
        texto: e instanceof ErroApi ? e.mensagem : 'Não consegui puxar agora.',
      }),
  });

  async function sair() {
    await authApi.logout().catch(() => undefined);
    limpar();
    useSussurros.getState().limpar();
    navegar('/login', { replace: true });
  }

  const atual = detalhe.data;

  return (
    // `h-full`, e não `h-screen`: a estação passou a viver debaixo da faixa do topo, e
    // uma altura de tela inteira aqui empurraria os últimos 40 px para fora da janela.
    <div className="flex h-full min-h-0 bg-white dark:bg-neutral-950">
      {/* Em pausa a tela inteira sai do caminho: quem está parado não responde cliente
          "de rapidinho", que é o que faz o tempo da conversa correr sem ninguém lendo. */}
      {status === 'pausa' && (
        <ModalPausa
          motivo={motivoPausa}
          desdeIso={pausaDesde}
          voltando={presenca.isPending}
          onVoltar={() => presenca.mutate({ novo: 'online' })}
        />
      )}
      <BarraAtendente
        status={status}
        motivoPausa={motivoPausa}
        invisivel={invisivel}
        resumo={resumo.data}
        desempenho={desempenho.data}
        minhas={minhas.data ?? []}
        selecionada={selecionada}
        puxando={puxar.isPending}
        onStatus={(novo, motivo) => presenca.mutate({ novo, motivo })}
        onInvisivel={(valor) => trocarInvisivel.mutate(valor)}
        onPuxar={() => puxar.mutate()}
        onSelecionar={(id) => {
          setSelecionada(id);
          setAnteriorId(null);
        }}
        onSair={() => void sair()}
      />

      {/* min-h-0 pelo mesmo motivo da tela de Atendimentos: sem ele o fio de mensagens
          cresce até o tamanho do conteúdo e deixa de rolar. */}
      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        {aviso && (
          <div className="px-4 pt-3">
            <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>
          </div>
        )}

        {!atual ? (
          <TudoNaPaz />
        ) : anteriorId ? (
          <ConversaAnterior
            conversa={anterior.data}
            carregando={anterior.isPending}
            onFechar={() => setAnteriorId(null)}
          />
        ) : (
          <>
            <CabecalhoConversa
              conversa={atual}
              acoes={
                atual.status !== 'encerrada' && (
                  <div className="flex gap-2">
                    {/*
                      Do lado do atendente o canal serve para pedir ajuda, então o botão
                      diz isso: o recado vai para os supervisores do setor (e para quem
                      já estiver falando no canal daquele atendimento).
                    */}
                    <Botao
                      variante="secundario"
                      onClick={() => abrirSussurro(atual.id, atual.protocolo)}
                      title="Falar com a supervisão sobre este atendimento (o cliente não vê)"
                    >
                      <Ear size={15} /> Chamar supervisão
                    </Botao>
                    <Botao variante="secundario" onClick={() => setTransferindo(true)}>
                      <Share2 size={15} /> Transferir
                    </Botao>
                    <Botao variante="perigo" onClick={() => setEncerrando(true)}>
                      <CheckCircle2 size={15} /> Encerrar
                    </Botao>
                  </div>
                )
              }
            />
            <Conversa
              conversa={atual}
              mensagens={atual.mensagens}
              respostasRapidas={(rapidas.data ?? [])
                .filter((r) => r.ativo)
                .map((r) => ({ id: r.id, titulo: r.titulo, texto: r.conteudo }))}
              onMudou={recarregar}
            />
          </>
        )}
      </main>

      {/*
        A coluna da direita é sempre do cliente do atendimento em curso, mesmo com um
        protocolo antigo ocupando o centro — é dela que sai a lista para navegar, e
        desmontá-la durante o carregamento faria a aba voltar sozinha para Perfil.
      */}
      {atual && (
        <PainelCliente
          conversa={atual}
          anteriorAberto={anteriorId}
          onAbrirAnterior={(id) => setAnteriorId(id)}
          onVincular={() => setVinculando(true)}
          onAnotou={recarregar}
        />
      )}

      {atual && vinculando && (
        <ModalVincular
          conversaId={atual.id}
          onFechar={() => setVinculando(false)}
          onVinculado={() => {
            setVinculando(false);
            recarregar();
            setAviso({ tipo: 'sucesso', texto: 'Cadastro vinculado ao atendimento.' });
          }}
        />
      )}

      {atual && transferindo && (
        <ModalTransferir
          conversa={atual}
          setores={(setores.data ?? []).map((s) => ({ id: s.id, nome: s.nome }))}
          onFechar={() => setTransferindo(false)}
          onPronto={() => {
            setTransferindo(false);
            setSelecionada(null);
            recarregar();
            setAviso({ tipo: 'sucesso', texto: 'Atendimento transferido.' });
          }}
        />
      )}

      {atual && encerrando && (
        <ModalEncerrar
          conversa={atual}
          motivos={(motivos.data ?? []).flatMap((c) =>
            c.motivos.map((m) => ({ id: m.id, nome: `${c.nome} · ${m.nome}` })),
          )}
          onFechar={() => setEncerrando(false)}
          onPronto={(escopo) => {
            setEncerrando(false);
            setSelecionada(null);
            recarregar();
            setAviso({
              tipo: 'sucesso',
              texto:
                escopo === 'apenas_eu'
                  ? 'Você saiu do atendimento. Quem ficou continua com o cliente.'
                  : 'Atendimento encerrado.',
            });
          }}
        />
      )}
    </div>
  );
}

/** Protocolo antigo em leitura, ocupando o centro até o atendente fechar no X. */
function ConversaAnterior({
  conversa,
  carregando,
  onFechar,
}: {
  conversa: ConversaDetalhe | undefined;
  carregando: boolean;
  onFechar: () => void;
}) {
  if (carregando || !conversa) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-neutral-500">
        Abrindo o atendimento anterior…
      </div>
    );
  }

  return (
    <>
      <header className="flex items-center justify-between gap-2 border-b border-amber-300 bg-amber-50 px-4 py-2.5 dark:border-amber-800 dark:bg-amber-950">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-amber-900 dark:text-amber-100">
            Atendimento anterior · somente leitura
          </p>
          <p className="truncate font-mono text-[11px] text-amber-700 dark:text-amber-300">
            {conversa.protocolo}
            <span className="font-sans">
              {' · '}
              {conversa.setor?.nome ?? 'sem setor'} ·{' '}
              {new Date(conversa.iniciadaEm).toLocaleDateString('pt-BR')}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={onFechar}
          aria-label="Voltar para o atendimento atual"
          title="Voltar para o atendimento atual"
          className="rounded-lg p-1.5 text-amber-800 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900"
        >
          <X size={18} />
        </button>
      </header>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
        {conversa.mensagens.map((m) => (
          <div key={m.id} className={m.de === 'cliente' ? '' : 'text-right'}>
            <p className="text-[10px] text-neutral-500">
              {m.de === 'cliente'
                ? 'Cliente'
                : m.de === 'atendente'
                  ? (m.autorNome ?? 'Atendente')
                  : m.de === 'bot'
                    ? 'Atendimento automático'
                    : 'Sistema'}{' '}
              · {new Date(m.criadaEm).toLocaleString('pt-BR')}
            </p>
            <p
              className={`inline-block max-w-[75%] rounded-lg px-3 py-1.5 text-left text-sm whitespace-pre-wrap ${
                m.de === 'cliente'
                  ? 'bg-neutral-100 dark:bg-neutral-800'
                  : m.tipo === 'nota_interna'
                    ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100'
                    : 'bg-marca-600 text-white'
              }`}
            >
              {m.texto ?? (m.midia ? `[anexo: ${m.midia.nomeOriginal}]` : '')}
            </p>
          </div>
        ))}
        {conversa.mensagens.length === 0 && (
          <p className="py-8 text-center text-sm text-neutral-500">
            Este atendimento não tem mensagens registradas.
          </p>
        )}
      </div>
    </>
  );
}

/**
 * A tela de quem está sem atendimento.
 *
 * Era um parágrafo explicando o funcionamento da fila — texto que serve no primeiro dia e
 * vira ruído no milésimo. Quem está de plantão olha para cá o dia inteiro, e o que a tela
 * precisa dizer é só que não há nada pendente (pedido de 29/09, no espírito do Blip).
 *
 * Quem aparece é a mascote da Maxx, de olhos fechados: silêncio aqui é coisa boa, não erro
 * nem espera. `select-none` e `draggable={false}` porque arrastar a imagem sem querer,
 * numa tela que fica aberta o dia todo, só atrapalha.
 */
function TudoNaPaz() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
      <img
        src="/mascote.png"
        alt=""
        draggable={false}
        // Sem `alt`: a imagem é decoração do recado que vem logo abaixo, e um leitor de
        // tela que a descrevesse diria duas vezes a mesma coisa.
        //
        // Pequena de propósito (112px): é um selo de "não há nada aqui", não uma
        // ilustração de abertura. Grande, ela virava o assunto da tela e fazia a ausência
        // de atendimento parecer mais importante do que o atendimento.
        className="max-h-28 w-auto max-w-[40%] select-none object-contain"
      />
      <p className="text-2xl font-medium text-neutral-400 dark:text-neutral-500">Tudo na paz…</p>
    </div>
  );
}
